import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import ReportService from '../../services/ReportService';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { PaymentReportDto, PaymentReportRequest } from "@ontime/types";
import { DownloadExcel } from './ReportDialog';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectUserOrganizationsList } from '../../stateManagement/organizationUserSlice';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import InfoComponent from '../../customComponents/InfoComponent';

const PaymentReport: React.FC<{
    loading: boolean;
    setLoading: (input: boolean) => void;
}> = ({ loading, setLoading }) => {

    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<PaymentReportDto[]>([]);

    const GetPaymentReport = async (filter: PaymentReportRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetPaymentReport(filter);
            setData(response.data.result);
        } catch (error) {
            enqueueSnackbar('Failed to fetch place', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const GetPaymentReportExcel = async (filter: PaymentReportRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetPaymentReportExcel(filter);
            DownloadExcel('PaymentReport', response.data);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to fetch Report', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const organizationList = useSelector(selectUserOrganizationsList);

    const AppointmentByClientIdSchema = z.object({
        organizationId: z.string(),
        creationTimeFrom: z.string().optional(),
        creationTimeTo: z.string().optional(),
    });

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<PaymentReportRequest>({
        resolver: zodResolver(AppointmentByClientIdSchema),
        defaultValues: {
            organizationId: organizationList?.[0].organizationId ?? ''
        },
    });

    const search = (
        <Box gap={2} m={2}>
            <form onSubmit={handleSubmit(GetPaymentReport)} noValidate>
                <InfoComponent infoContent='Report filters'>
                    <Box display="flex" justifyContent="space-evenly" flexWrap='wrap' gap={2} mt={2}>
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.organizationId} disabled={loading}>
                            <InputLabel>Place</InputLabel>
                            <Controller
                                name="organizationId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Place"
                                        {...field}
                                    >
                                        {organizationList.map((item) => (
                                            <MenuItem key={item.organizationId} value={item.organizationId}>
                                                {item.organizationName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors.organizationId?.message}</FormHelperText>
                        </FormControl>

                        <Controller
                            name="creationTimeFrom"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="From"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                        <Controller
                            name="creationTimeTo"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="To"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                    </Box>
                </InfoComponent>
                <CustomFormAction>
                    <CustomButton lable='Show Report' type='submit' disabled={loading} infoContent={`Click to show report`} />
                    <CustomButton lable='Download Excel' onClick={handleSubmit(GetPaymentReportExcel)} disabled={loading} infoContent={`Click to download excel`} />
                </CustomFormAction>
            </form>
        </Box>
    )

    const paymentReportType: GridColumnType<PaymentReportDto> = {
        appointmentDate: { type: 'Text', header: 'Appointment Date' },
        appointmentTime: { type: 'Text', header: 'Appointment Time' },
        paymentDate: { type: 'Text', header: 'Payment Date' },
        paymentTime: { type: 'Text', header: 'Payment Time' },
        appointmentAmount: { type: 'Price', header: 'Appointment Amount' },
        paymentAmount: { type: 'Price', header: 'Payment Amount' },
        paymentDiscountAmount: { type: 'Price', header: 'Discount' },
        paymentSurchargeAmount: { type: 'Price', header: 'Surcharge' },
        paymentTip: { type: 'Price', header: 'Tip' },
        paymentTaxPercent: { type: 'Integer', header: 'Tax %' },
        paymentFinalAmount: { type: 'Price', header: 'Final Amount' },
        paymentUsedPointsCount: { type: 'Integer', header: 'Used Points' },
        paymentReductionAmountByUsedPoints: { type: 'Price', header: 'Reduction by Points' },
        paymentEarnedPoints: { type: 'Price', header: 'Earned Points' },
        client: { type: 'Text', header: 'Client Name' },
        phoneNumber: { type: 'Text', header: 'Client Phone' },
    };

    return (
        <GridTable<PaymentReportDto, PaymentReportRequest, PaymentReportDto>
            gridTitle='Payments Report'
            list={data}
            inputType={paymentReportType}
            customSearch={search}
        />
    );
};

export default PaymentReport;
